/**
 * Echo Hunt -- Corpus tab: the corpus browser + the index-pass runner (P5).
 *
 * This replaces P4's dev readout now that the index pass exists. What it shows:
 *  - the §17 supply check: usable total, the filtered split by reason, and the event-type distribution
 *    (which P6's fake generator must match, §5.3) plus the mean oddity count (§5.2);
 *  - a table of bullets, raw text next to the self-contained rewrite, filterable by
 *    `filterReason` / `eventType` / text search;
 *  - the incremental index runner, with progress and a cancel button, and an explicit count of what a
 *    run WOULD do (`missing` + `changed`) so "re-running is a no-op" is visible rather than assumed;
 *  - a per-bullet "re-index this dream" action, and a manual `filterReason` override (§P5).
 *
 * NOTE (v1, decided 2026-10-20): there is no lucidity column. Judging which moment of a dream was
 * lucid is not reliable, and nothing downstream consumed it.
 */
import React, {useMemo, useState} from "react";
import {observer_mgl} from "mobx-graphlink";
import {Button, Column, Row, Select, Text, TextInput} from "react-vcomponents";
import {ProgressSpinner} from "../../../Utils/ReactComponents/ProgressSpinner.js";
import {ScrollView} from "react-vscrollview";
import {InfoButton, Link, RunInAction_Set, TimeToString} from "web-vcore";
import {EchoCorpusBullet} from "../../../Server/DB/echoCorpusBullets/@EchoCorpusBullet.js";
import {BulletFilterReason_values, BulletEventType_values} from "../../../EchoHunt/Types.js";
import {
	GetCorpusIndexReport,
	IndexCorpus_Live,
	IndexRunControl,
	ResolveIndexerModel,
	SetCorpusBulletFilterReason_Live,
} from "../../../EchoHunt/Index_Live.js";
import {GetCheckOptionsFromProfile} from "../../../EchoHunt/Bullets_Live.js";
import {CheckBullet, NormalizeHouseStyle} from "../../../EchoHunt/Normalize.js";
import {CorpusSortBy, CorpusSortBy_descriptions, CorpusSortBy_labels, CorpusSortBy_values, SortCorpusRows} from "../../../EchoHunt/CorpusSort.js";
import {store} from "../../../Store/index.js";

const MAX_ROWS_SHOWN = 300;
const ALL = "(all)";
const USABLE = "(usable)";

export const CorpusPanel = observer_mgl(()=>{
	const report = GetCorpusIndexReport();

	// the filter/sort controls live in the persisted mobx store tree (`store.main.tools.echoHunt`),
	// alongside the tab selection, so they survive a reload without a separate storage mechanism
	const uiState = store.main.tools.echoHunt;
	const {corpusFilterReason: filterReason, corpusFilterEventType: filterEventType, corpusSearch: search, corpusShowRaw: showRaw, corpusSortBy: sortBy, corpusSortDescending: descending} = uiState;

	const setFilterReason = (value: string)=>RunInAction_Set(()=>uiState.corpusFilterReason = value);
	const setFilterEventType = (value: string)=>RunInAction_Set(()=>uiState.corpusFilterEventType = value);
	const setSearch = (value: string)=>RunInAction_Set(()=>uiState.corpusSearch = value);
	const setShowRaw = (value: boolean)=>RunInAction_Set(()=>uiState.corpusShowRaw = value);
	const setSortBy = (value: CorpusSortBy)=>RunInAction_Set(()=>uiState.corpusSortBy = value);
	const setDescending = (value: boolean)=>RunInAction_Set(()=>uiState.corpusSortDescending = value);

	// the run's control object lives in component state, so a re-render (which happens constantly
	// while the run writes rows) does not lose the cancel button's target
	const [control, setControl] = useState<IndexRunControl|n>(null);
	const [runLog, setRunLog] = useState<string[]>([]);
	const [rerunningDreamId, setRerunningDreamId] = useState<string|n>(null);

	const running = (control != null && !control.finished) || rerunningDreamId != null;
	const pendingCount = report.missing.length + report.changed.length;

	const rows = useMemo(()=>{
		// the order is user-controlled and TOTAL (see `CorpusSort.ts`), rather than the old
		// `indexedAt`-descending default, which reshuffled the table after every incremental run
		return SortCorpusRows([...report.rowsById.values()], sortBy, descending);
	}, [report.rowsById, sortBy, descending]);

	const filteredRows = useMemo(()=>{
		const search_lower = search.trim().toLowerCase();
		return rows.filter(row=>{
			if (filterReason != ALL) {
				const rowReason = row.filterReason ?? USABLE;
				if (rowReason != filterReason) return false;
			}
			if (filterEventType != ALL && (row.eventType ?? "") != filterEventType) return false;
			if (search_lower.length > 0) {
				const haystack = `${row.rawText ?? ""} ${row.selfContainedText ?? ""} ${row.heading ?? ""}`.toLowerCase();
				if (!haystack.includes(search_lower)) return false;
			}
			return true;
		});
	}, [rows, filterReason, filterEventType, search]);

	const startRun = (options?: {dreamLimit?: number, onlyDreamId?: string})=>{
		const nextControl = new IndexRunControl();
		nextControl.total = report.sourceBullets.length;
		nextControl.done = report.upToDate;
		setControl(nextControl);
		setRunLog([]);

		return IndexCorpus_Live(nextControl, options).then(result=>{
			setRunLog([
				`Indexed ${result.dreamsIndexed} dream(s); wrote ${result.bulletsWritten} bullet row(s).`,
				...result.errors,
			]);
		}).catch(ex=>{
			setRunLog([`ERROR: ${ex}`]);
		}).finally(()=>{
			setRerunningDreamId(null);
		});
	};

	return (
		// The header stays pinned and ONLY the rows scroll. That is deliberate: the raw/processed text
		// toggle has to be reachable while looking at a bullet far down the list, otherwise comparing the
		// two forms of the same bullet means scrolling back up every time.
		<Column style={{flex: 1, minHeight: 0, padding: 10}}>
			{/* header (non-scrolling) */}
			<Column style={{flexShrink: 0}}>
				<Row center style={{gap: 8, flexWrap: "wrap"}}>
					<Text style={{fontWeight: "bold"}}>Corpus</Text>
					<InfoButton text={[
						"Echo Hunt's corpus is built ONLY from each dream's `publicText` field (the bulleted markdown",
						"you posted publicly) -- never from longText/shortText.",
						"",
						"THE INDEX PASS (one model call per DREAM, so references like \"she said again\" resolve):",
						"  selfContainedText -- the bullet rewritten as a standalone event, in house style.",
						"  eventType         -- a coarse tag; fakes must match this distribution (§5.3).",
						"  oddityCount       -- how many out-of-place objects the core event contains (caps at 4);",
						"                       seeds the fake generator's concept count (§5.2).",
						"  filterReason      -- why a bullet is unusable, or blank when it is usable (§4.4).",
						"",
						"A deterministic post-pass then normalises the text and re-checks it. Anything still breaking",
						"house style is re-asked ONCE, then marked `styleViolation`.",
						"",
						"The pass is INCREMENTAL: a bullet whose text is unchanged is skipped, so re-running is a no-op",
						"until you edit a journal post. Editing one bullet re-indexes only its own dream.",
						"",
						"`Rewrite` / `Raw` switches every row between the indexed rewrite and the original journal text,",
						"so the two forms of one bullet can be compared without losing your place.",
						"",
						"There is NO lucidity column (decided 2026-10-20): judging which moment of a dream was lucid",
						"is not reliable enough, and nothing downstream used it.",
					].join("\n")}/>
				</Row>

				{/* supply check (§17) */}
				<Column mt={8} style={{gap: 2}}>
					<Text>
						{`Journal: ${report.sourceBullets.length} bullet(s) across ${new Set(report.sourceBullets.map(a=>a.dreamId)).size} dream(s)  |  cached rows: ${report.rowsById.size}  |  up to date: ${report.upToDate}`}
					</Text>
					<Text style={{opacity: .85}}>
						{`Usable: ${report.summary.usableCount}`}
						{`  |  mean oddity: ${report.summary.meanOddityCount.toFixed(2)}`}
						{Object.entries(report.summary.byFilterReason).length > 0 &&
							`  |  filtered: ${Object.entries(report.summary.byFilterReason).map(([reason, count])=>`${reason} ${count}`).join(", ")}`}
					</Text>
					<Text style={{opacity: .85}}>
						{`Event types: ${BulletEventType_values.map(a=>`${a} ${report.summary.byEventType[a] ?? 0}`).join(", ")}`}
					</Text>
					{pendingCount > 0 &&
						<Text style={{color: "orange"}}>
							{`${pendingCount} bullet(s) need a model call (${report.missing.length} new, ${report.changed.length} changed).`}
						</Text>}
					{report.sourceBullets.length == 0 &&
						<Text mt={4} style={{color: "orange", maxWidth: 700}}>
							{"No bullets found. Add a post to a dream's `publicText` field (Journal > expand a dream > Public text)."}
						</Text>}
				</Column>

				{/* index runner */}
				<Row mt={10} center style={{gap: 8, flexWrap: "wrap"}}>
					<Button text={pendingCount == 0 ? "Index corpus (nothing to do)" : `Index corpus (${pendingCount} bullet(s))`}
						enabled={!running && pendingCount > 0} onClick={()=>startRun()}/>
					<Button text="Index 20 dreams" enabled={!running && pendingCount > 0} onClick={()=>startRun({dreamLimit: 20})}/>
					{running &&
						<>
							<Button text="Cancel" onClick={()=>control?.cancel()}/>
							<ProgressSpinner/>
							<Text style={{opacity: .8}}>{`${control?.done ?? 0} / ${control?.total ?? 0} bullets`}</Text>
						</>}
					<Text style={{opacity: .6, fontSize: 11}}>{`indexer model: ${ResolveIndexerModel() || "(global)"}`}</Text>
				</Row>
				{runLog.length > 0 &&
					<Column mt={6} style={{gap: 2}}>
						{runLog.slice(0, 15).map((line, i)=><Text key={i} style={{fontSize: 12, color: line.startsWith("ERROR") ? "red" : undefined}}>{line}</Text>)}
					</Column>}
				{report.staleRows.length > 0 &&
					<Text mt={4} style={{fontSize: 12, color: "orange"}}>
						{`${report.staleRows.length} cached row(s) no longer match any journal bullet; the next run deletes them.`}
					</Text>}

				{/* filters */}
				<Row mt={14} center style={{gap: 10, flexWrap: "wrap"}}>
					<Text>Filter reason:</Text>
					<Select displayType="dropdown" style={{width: 160}} value={filterReason} onChange={setFilterReason}
						options={[ALL, USABLE, ...BulletFilterReason_values].map(a=>({name: a, value: a}))}/>
					<Text ml={6}>Event type:</Text>
					<Select displayType="dropdown" style={{width: 130}} value={filterEventType} onChange={setFilterEventType}
						options={[ALL, ...BulletEventType_values].map(a=>({name: a, value: a}))}/>
					<TextInput ml={6} style={{width: 160}} value={search} placeholder="search text…" onChange={setSearch}/>
					{filteredRows.length > MAX_ROWS_SHOWN &&
						<Text style={{opacity: .7, fontSize: 12}}>{`showing first ${MAX_ROWS_SHOWN} of ${filteredRows.length}`}</Text>}
				</Row>

				{/* sort (view settings, held in the persisted mobx store tree -- see EchoHuntState) */}
				<Row mt={8} center style={{gap: 10, flexWrap: "wrap"}}>
					<Text>Sort by:</Text>
					<Select displayType="dropdown" value={sortBy} onChange={val=>{
						setSortBy(val as CorpusSortBy);
					}} options={CorpusSortBy_values.map(a=>({name: CorpusSortBy_labels[a], value: a}))}/>
					<Select displayType="button bar" value={descending ? "desc" : "asc"} onChange={val=>{
						setDescending(val == "desc");
					}} options={[{name: "Ascending", value: "asc"}, {name: "Descending", value: "desc"}]}/>
					<InfoButton text={[
						`Sort order: ${CorpusSortBy_labels[sortBy]} (${descending ? "descending" : "ascending"})`,
						CorpusSortBy_descriptions[sortBy],
						"",
						"Bullets that share the sort key are ordered by journal path (entry / segment / bullet",
						"number), so a night's bullets always read forwards -- including when sorting descending.",
						"`path` compares the bullet NUMBER numerically, so bullet 10 comes after bullet 2.",
						"",
						"A missing dream date sorts as the oldest rather than jumping to the top.",
					].join("\n")}/>

					{/* the raw/rewrite switch: a button bar rather than a checkbox, because the point is to
					    flip it repeatedly while reading a row far down the list */}
					<Text ml={10}>Text:</Text>
					<Select displayType="button bar" value={showRaw ? "raw" : "rewrite"} onChange={val=>{
						setShowRaw(val == "raw");
					}} options={[
						{name: "Raw", value: "raw"},
						{name: "Rewrite", value: "rewrite"},
					]}/>
					<Text style={{opacity: .6, fontSize: 11}}>
						{showRaw ? "original journal text" : "indexed standalone rewrite"}
					</Text>
				</Row>
			</Column>

			{/* rows (the only scrolling part) */}
			<ScrollView style={{flex: 1, minHeight: 0, marginTop: 8}} contentStyle={{display: "flex", flexDirection: "column", gap: 6, paddingRight: 6}}>
				{filteredRows.slice(0, MAX_ROWS_SHOWN).map(row=>
					<CorpusRow key={row.id} row={row} showRaw={showRaw} running={running}
						rerunning={rerunningDreamId == row.dreamId}
						onReindexDream={()=>{
							setRerunningDreamId(row.dreamId);
							startRun({onlyDreamId: row.dreamId}).catch(()=>{});
						}}/>,
				)}
				{filteredRows.length == 0 && report.rowsById.size > 0 &&
					<Text style={{opacity: .7}}>{"No bullets match these filters."}</Text>}
				{report.rowsById.size == 0 &&
					<Text style={{opacity: .7}}>{"Nothing indexed yet -- run the index pass above."}</Text>}
			</ScrollView>
		</Column>
	);
});

const CorpusRow = observer_mgl((props: {row: EchoCorpusBullet, showRaw: boolean, running: boolean, rerunning: boolean, onReindexDream: ()=>void})=>{
	const {row, showRaw, running, rerunning, onReindexDream} = props;
	const opts = GetCheckOptionsFromProfile();

	const text = showRaw ? (row.rawText ?? "") : (row.selfContainedText ?? "");
	const usable = row.filterReason == null;
	// re-check whatever is displayed, so a bullet that LOOKS clean in the table but is not is visible
	const violations = CheckBullet(showRaw ? NormalizeHouseStyle(row.rawText ?? "") : (row.selfContainedText ?? ""), opts);

	return (
		<Column style={{
			padding: 6, borderRadius: 4,
			background: usable ? "rgba(20,45,20,.6)" : "rgba(60,35,10,.6)",
		}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontSize: 11, opacity: .6}}>{row.bulletId ?? row.id}</Text>
				{row.date != null && <Text style={{fontSize: 11, opacity: .75}} title="dream date">{TimeToString(row.date, {date: 1})}</Text>}
				{row.heading != null && <Text style={{fontSize: 11, color: "lightblue"}}>{`### ${row.heading}`}</Text>}
				{row.eventType != null && <Text style={{fontSize: 11, color: "lightgreen"}}>{row.eventType}</Text>}
				{(row.oddityCount ?? 0) > 0 && <Text style={{fontSize: 11, color: "violet"}}>{`oddity ${row.oddityCount}`}</Text>}
				{!usable && <Text style={{fontSize: 11, color: "orange"}}>{row.filterReason}</Text>}
			</Row>

			<Text style={{fontSize: 13, marginTop: 3}}>{text.length > 0 ? text : "(no text)"}</Text>

			{violations.length > 0 &&
				<Text style={{fontSize: 11, color: "orange", marginTop: 2}}>{`check: ${violations.map(a=>a.kind).join(", ")}`}</Text>}
			{(row.postPassViolations ?? []).length > 0 &&
				<Text style={{fontSize: 11, color: "orange", marginTop: 2}}>{`post-pass: ${(row.postPassViolations ?? []).join(" | ")}`}</Text>}

			<Row mt={4} center style={{gap: 8, flexWrap: "wrap"}}>
				{/* `dreamId` is `entryId/segmentId`; the journal page selects by ENTRY id, so split it. The Link
				    computes its own URL from the store, so this navigates to `journal/<entry id>` as usual. */}
				{row.dreamId != null &&
					<Link actionFunc={s=>{
						const entryId = row.dreamId.split("/")[0];
						if (entryId.length == 0) return;
						s.main.page = "journal";
						s.main.journal.selectedJournalEntry = entryId;
					}}>
						<Button text="Show dream"/>
					</Link>}
				<Button text={rerunning ? "Re-indexing…" : "Re-index dream"} enabled={!running} onClick={onReindexDream}/>
				<Text style={{opacity: .6, fontSize: 11}}>Manual filter verdict:</Text>
				<Select displayType="dropdown" style={{width: 150}} value={row.filterReason ?? USABLE} onChange={val=>{
					const reason = val == USABLE ? null : val;
					SetCorpusBulletFilterReason_Live(row.id, reason).catch(ex=>console.error("Failed to set filter reason:", ex));
				}} options={[USABLE, ...BulletFilterReason_values].map(a=>({name: a, value: a}))}/>
			</Row>
		</Column>
	);
});
